import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { MessageSquare, Star, ThumbsUp, TrendingUp } from 'lucide-react';
import { StatCard, WidgetPanel, CustomerRetentionWidget } from './DashboardShared';
import { customerFeedback } from '../../../data/store';
import { useSharedStore } from '../../../hooks/useSharedStore';

export const CustomerFeedbackModule = ({ org }) => {
  const { version } = useSharedStore();

  // Real feedback for this organization. This module previously invented its
  // figures — a fixed 4.8 rating, 124 reviews, and reviews signed "Alice",
  // "Bob" and "Charlie" — for any org that had made a single sale, and showed
  // them as that shop's own data. The `/feedback` endpoint and the
  // `customerFeedback` store array already existed; nothing was reading them.
  const feedback = useMemo(
    () => customerFeedback.filter((f) => f.orgId === org.id),
    [org.id, version],
  );

  const metrics = useMemo(() => {
    const total = feedback.length;
    const empty = {
      averageRating: 0,
      totalReviews: 0,
      positiveFeedbackPct: 0,
      recentFeedback: [],
      chartData: [
        { label: '5 Stars', subtitle: 'Excellent', value: 0, max: 1, color: '#10b981' },
        { label: '4 Stars', subtitle: 'Good', value: 0, max: 1, color: '#3b82f6' },
        { label: '<3 Stars', subtitle: 'Needs Improvement', value: 0, max: 1, color: '#f43f5e' },
      ],
    };
    if (!total) return empty;

    const ratingOf = (f) => Number(f.rating) || 0;
    const sum = feedback.reduce((acc, f) => acc + ratingOf(f), 0);
    const five = feedback.filter((f) => ratingOf(f) === 5).length;
    const four = feedback.filter((f) => ratingOf(f) === 4).length;
    const low = feedback.filter((f) => ratingOf(f) > 0 && ratingOf(f) < 3).length;

    return {
      averageRating: Number((sum / total).toFixed(1)),
      totalReviews: total,
      // "Positive" is 4 stars and above, stated here so the number can be
      // explained if anyone asks how it was calculated.
      positiveFeedbackPct: Math.round(((five + four) / total) * 100),
      recentFeedback: [...feedback]
        .sort((a, b) => new Date(b.createdAt || b.visitDate || 0) - new Date(a.createdAt || a.visitDate || 0))
        .slice(0, 3)
        .map((f) => ({
          id: f.id,
          customer: f.name || 'Walk-in customer',
          rating: ratingOf(f),
          comment: f.comments || '',
          date: f.createdAt || f.visitDate || null,
        })),
      chartData: [
        { label: '5 Stars', subtitle: 'Excellent', value: five, max: total, color: '#10b981' },
        { label: '4 Stars', subtitle: 'Good', value: four, max: total, color: '#3b82f6' },
        { label: '<3 Stars', subtitle: 'Needs Improvement', value: low, max: total, color: '#f43f5e' },
      ],
    };
  }, [feedback]);

  return (
    <div className="flex flex-col gap-4 mt-6">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-2">
        <MessageSquare size={16} className="text-[var(--theme-primary)]" />
        Customer Feedback & Ratings
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Star} label="Average Rating" value={`${metrics.averageRating} / 5.0`} iconColor="#f59e0b" trend={2} />
        <StatCard icon={MessageSquare} label="Total Reviews" value={metrics.totalReviews} iconColor="#8b5cf6" />
        <StatCard icon={ThumbsUp} label="Positive Feedback" value={`${metrics.positiveFeedbackPct}%`} iconColor="#10b981" trend={1} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WidgetPanel title="Recent Feedback" className="lg:col-span-2" iconColor="text-sky-500">
          <ul className="divide-y divide-[var(--theme-tertiary)]">
            {metrics.recentFeedback.map((f) => (
              <li key={f.id} className="py-3 flex flex-col gap-1 text-[11px] group cursor-pointer hover:bg-slate-50 px-3 -mx-3 rounded-lg transition-colors">
                <div className="flex items-center justify-between">
                  <p className="font-bold text-[#0d0d0d]">{f.customer}</p>
                  <div className="flex items-center gap-0.5 text-amber-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={12} className={i < f.rating ? 'fill-current' : 'text-slate-200'} />
                    ))}
                  </div>
                </div>
                <p className="text-slate-600">"{f.comment}"</p>
                <p className="text-[10px] font-semibold text-slate-400 mt-1 uppercase tracking-widest">{new Date(f.date).toLocaleDateString()}</p>
              </li>
            ))}
          </ul>
        </WidgetPanel>

        <WidgetPanel title="Rating Distribution">
          <div className="flex flex-col gap-4 mt-2 h-full min-h-[250px]">
             <CustomerRetentionWidget data={metrics.chartData} />
          </div>
        </WidgetPanel>
      </div>
    </div>
  );
};

CustomerFeedbackModule.propTypes = {
  org: PropTypes.object.isRequired,
};
